import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../lib/api";
import { Field, bLabel } from "../scouting/parts";
import { compatWarnings, LABEL_DB_URL, labelSearchUrl } from "./compat";
import { ArrowLeft, Loader2, Plus, Trash2, SprayCan } from "lucide-react";

const inp = "w-full h-12 px-3 border border-stone-300";

const Card = ({ title, children }) => (
  <div className="bg-white border border-stone-200 p-4 space-y-3">
    <div className="font-bold uppercase tracking-wide text-sm">{title}</div>
    {children}
  </div>
);

export default function OrderBuilder() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const preselected = useMemo(() => (state?.blocks || []).map((b) => ({
    id: b.block_id || b.id, ranch_number: b.ranch_number, vineyard_name: b.vineyard_name || b.name,
    block: b.block, variety: b.variety, acres: b.acres,
  })), [state]);

  const [ranches, setRanches] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [windows, setWindows] = useState(null);
  const [suggest, setSuggest] = useState(null);
  const [blocks, setBlocks] = useState(preselected);
  const [ranchNumber, setRanchNumber] = useState(state?.ranch_number || preselected[0]?.ranch_number || "");
  const rec = state?.rec || null;
  const [products, setProducts] = useState([{ kind: "pesticide", material_id: "", rate: "", rate_unit: "" }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [gpaMode, setGpaMode] = useState("100");
  const [f, setF] = useState({
    target: "Powdery mildew", reason: "", date: "", planned_start: "", county: "", site_permit_id: "",
    manager: "", supervisor: "", applicator: "", pca: "",
    pattern: "every_row", start_row: "", direction: "", row_numbers: "",
    tractor: "", sprayer: "", method: "Airblast", nozzle_setup: "", nozzle_count: "",
    pressure_psi: "", speed_mph: "", sprayer_gpm: "", row_spacing_ft: "",
    gpa: "100", tank_gal: "500", treated_acres: "",
    field_instructions: "", sensitive_areas: "", notes: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get("/materials").then(({ data }) => setMaterials(data.filter((m) => m.active)));
    api.get("/ranches").then(({ data }) => setRanches(data));
  }, []);
  useEffect(() => {
    if (!rec || !ranches.length || blocks.length) return;
    const pre = ranches.filter((r) => rec.block_ids.includes(r.id))
      .map((r) => ({ id: r.id, ranch_number: r.ranch_number, vineyard_name: r.name, block: r.block, variety: r.variety, acres: r.acres }));
    setBlocks(pre);
    if (rec.targets?.length) setF((s) => ({ ...s, target: rec.targets.join(" + ") }));
  }, [ranches]); // eslint-disable-line
  useEffect(() => {
    if (!ranchNumber) return setWindows(null);
    api.get(`/pest/spray-windows?ranch_number=${ranchNumber}`).then(({ data }) => setWindows(data)).catch(() => setWindows(null));
  }, [ranchNumber]);

  const blocksKey = blocks.map((b) => b.id).sort().join(",");
  useEffect(() => {
    if (!blocksKey || !ranchNumber) return setSuggest(null);
    api.get(`/pest/spray-orders/suggest?ranch_number=${ranchNumber}&block_ids=${blocksKey}`)
      .then(({ data }) => {
        setSuggest(data);
        setProducts((cur) => {
          if (!cur.every((p) => !p.material_id)) return cur;
          const rows = data.defaults.map((d) => ({ kind: "pesticide", material_id: d.material_id, rate: "", rate_unit: d.rate_unit }));
          if (data.default_adjuvant) rows.push({ kind: "adjuvant", material_id: data.default_adjuvant.material_id, rate: "", rate_unit: data.default_adjuvant.rate_unit });
          return rows.length ? rows : cur;
        });
        if (data.pests.length) {
          setF((s) => (s.target === "Powdery mildew" ? { ...s, target: data.pests.join(" + ") } : s));
        }
      })
      .catch(() => setSuggest(null));
  }, [blocksKey, ranchNumber]); // eslint-disable-line

  const recIds = new Set((suggest?.recommended || []).map((m) => m.id));
  const sortedMaterials = [...materials].sort((a, b) => (recIds.has(b.id) ? 1 : 0) - (recIds.has(a.id) ? 1 : 0));
  const pesticideMats = sortedMaterials.filter((m) => (m.material_type || "Pesticide") === "Pesticide");
  const adjuvantMats = materials.filter((m) => m.material_type && m.material_type !== "Pesticide");

  const vineyards = [...new Map(ranches.map((r) => [r.ranch_number, r])).values()];
  const ranchBlocks = ranches.filter((r) => r.ranch_number === ranchNumber);
  const toggleBlock = (r) => setBlocks((cur) => cur.some((b) => b.id === r.id)
    ? cur.filter((b) => b.id !== r.id)
    : [...cur, { id: r.id, ranch_number: r.ranch_number, vineyard_name: r.name, block: r.block, variety: r.variety, acres: r.acres }]);

  const blockAcres = Math.round(blocks.reduce((s, b) => s + (b.acres || 0), 0) * 10) / 10;
  const autoTreated = blockAcres;
  const acres = Number(f.treated_acres) || autoTreated;
  const nGpa = Number(f.gpa) || 0;
  const nTank = Number(f.tank_gal) || 0;
  const totVol = Math.round(nGpa * acres);
  const fullTanks = nTank ? Math.floor(totVol / nTank) : 0;
  const finalTank = nTank ? Math.round(totVol - fullTanks * nTank) : 0;
  const effSpacing = (Number(f.row_spacing_ft) || 0) * (f.pattern === "every_other_row" ? 2 : 1);
  const calGpa = Number(f.speed_mph) && Number(f.sprayer_gpm) && effSpacing
    ? Math.round((495 * Number(f.sprayer_gpm)) / (Number(f.speed_mph) * effSpacing) * 10) / 10 : null;
  const mismatch = calGpa != null && nGpa > 0 && Math.abs(calGpa - nGpa) / nGpa > 0.05;

  const prodRows = products.map((p) => {
    const m = materials.find((x) => x.id === p.material_id);
    const rate = Number(p.rate) || 0;
    return { ...p, m, rate,
      perTank: nGpa && rate ? Math.round((rate * nTank) / nGpa * 100) / 100 : null,
      total: rate ? Math.round(rate * acres * 100) / 100 : null,
      outOfRange: m && rate && m.rate_min != null && m.rate_max != null && (rate < m.rate_min || rate > m.rate_max) };
  });
  const setProd = (i, upd) => setProducts((ps) => ps.map((p, j) => (j === i ? { ...p, ...upd } : p)));

  const windy = !!windows?.days?.slice(0, 3).some((d) => (d.windows || []).some((w) => w.rating === "POOR" && (w.reasons || []).some((r) => /wind/i.test(r))));
  const driftAgents = adjuvantMats.filter((m) => m.material_type === "Drift Reduction");
  const hasDrift = products.some((p) => materials.find((x) => x.id === p.material_id)?.material_type === "Drift Reduction");
  const needDrift = (f.sensitive_areas.trim() !== "" || windy) && !hasDrift && driftAgents.length > 0;
  const compat = compatWarnings(prodRows.map((p) => p.m).filter(Boolean));

  const valid = blocks.length > 0 && nGpa > 0 && nTank > 0
    && prodRows.some((p) => p.kind !== "adjuvant" && p.material_id && p.rate > 0)
    && !prodRows.some((p) => p.outOfRange)
    && (f.pattern !== "every_other_row" || f.start_row.trim());

  const missing = [];
  if (blocks.length === 0) missing.push("Select at least one block");
  if (nGpa <= 0) missing.push("Enter a Target GPA (Equipment & Calibration)");
  if (nTank <= 0) missing.push("Enter a Tank Size (Equipment & Calibration)");
  if (!prodRows.some((p) => p.kind !== "adjuvant" && p.material_id)) missing.push("Pick at least one approved material (Products)");
  else if (!prodRows.some((p) => p.kind !== "adjuvant" && p.material_id && p.rate > 0)) missing.push("Enter a rate for your product (Products)");
  prodRows.forEach((p) => { if (p.outOfRange) missing.push(`${p.m.product}: rate must be within the label range ${p.m.rate_min}–${p.m.rate_max} ${p.m.rate_unit || ""}`); });
  if (f.pattern === "every_other_row" && !f.start_row.trim()) missing.push("Every-other-row pattern needs a Start Row (Row Pattern)");

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const num = (v) => (v === "" || v == null ? null : Number(v));
      const { data } = await api.post("/pest/spray-orders", {
        ranch_number: ranchNumber, block_ids: blocks.map((b) => b.id),
        treated_acres: num(f.treated_acres) || autoTreated || null,
        target: f.target, reason: f.reason || null, date: f.date || null, planned_start: f.planned_start || null,
        county: f.county || null, site_permit_id: f.site_permit_id || null,
        manager: f.manager || null, supervisor: f.supervisor || null, applicator: f.applicator || null, pca: f.pca || null,
        pattern: f.pattern, start_row: f.start_row || null, direction: f.direction || null, row_numbers: f.row_numbers || null,
        tractor: f.tractor || null, sprayer: f.sprayer || null, method: f.method || null,
        nozzle_setup: f.nozzle_setup || null, nozzle_count: num(f.nozzle_count),
        pressure_psi: num(f.pressure_psi), speed_mph: num(f.speed_mph), sprayer_gpm: num(f.sprayer_gpm),
        row_spacing_ft: num(f.row_spacing_ft), gpa: nGpa, tank_gal: nTank,
        products: prodRows.filter((p) => p.material_id && p.rate > 0).map((p) => ({
          material_id: p.material_id, rate: p.rate, rate_unit: p.rate_unit || p.m?.rate_unit || "" })),
        field_instructions: f.field_instructions || null, sensitive_areas: f.sensitive_areas || null, notes: f.notes || null,
      });
      if (rec?.ids?.length) {
        for (const id of rec.ids) {
          try { await api.delete(`/pest/spray-orders/${id}`); } catch (e) { console.warn("Superseded order cleanup failed", e); }
        }
      }
      navigate(`/pest/orders/${data.id}`, { replace: true });
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not create the work order");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="order-builder">
      <div>
        <button onClick={() => navigate(-1)} className="text-xs font-bold uppercase text-stone-500 flex items-center gap-1 mb-2"><ArrowLeft size={13} /> Back</button>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>New Spray Work Order</h1>
        <p className="text-stone-500 text-sm mt-1">The label is the law — verify rates and PPE against the registered label before approval.</p>
      </div>

      <Card title="Site & Blocks">
        {rec && (
          <div className="bg-amber-50 border border-amber-300 p-3 text-xs font-semibold text-amber-900" data-testid="ob-rec-banner">
            Building from {rec.ids.length} scouting recommendation{rec.ids.length === 1 ? "" : "s"} ({rec.targets.join(" + ")}) — blocks with findings are pre-selected. Add or remove any blocks below.
          </div>
        )}
        <Field label="Ranch / Vineyard">
          <select data-testid="ob-ranch-select" value={ranchNumber} onChange={(e) => { setRanchNumber(e.target.value); setBlocks([]); }} className={inp}>
            <option value="">— Select Ranch —</option>
            {vineyards.map((r) => <option key={r.ranch_number} value={r.ranch_number}>{r.ranch_number} · {r.name}</option>)}
          </select>
        </Field>
        {ranchNumber && (
          <div className="flex flex-wrap gap-2" data-testid="ob-block-chips">
            {ranchBlocks.map((r) => (
              <button key={r.id} type="button" data-testid={`ob-block-${r.id}`} onClick={() => toggleBlock(r)}
                className={`h-11 px-4 text-sm font-semibold border ${blocks.some((b) => b.id === r.id) ? "bg-[#7C9673] text-white border-[#7C9673]" : "bg-white border-stone-300"}`}>
                {bLabel(r.block) || r.name}{r.acres ? ` · ${r.acres} ac` : ""}
              </button>
            ))}
          </div>
        )}
        {blocks.length > 0 && (
          <div className="text-sm bg-stone-50 border border-stone-200 p-3" data-testid="ob-block-summary">
            <b>{blocks[0].vineyard_name}</b> · {blocks.map((b) => bLabel(b.block)).join(", ")} · {blockAcres} ac
          </div>
        )}
        {suggest && blocks.length > 0 && (
          <div className={`text-xs p-3 border font-semibold ${suggest.farming_type === "Organic" ? "bg-[#7C9673]/10 border-[#7C9673] text-[#1F3D34]" : "bg-sky-50 border-sky-300 text-sky-900"}`} data-testid="ob-suggest-panel">
            <b>{suggest.farming_type} program</b>
            {suggest.pests.length > 0
              ? <> · Scouting found: <b>{suggest.pests.join(", ")}</b></>
              : " · No recent scouting findings — defaulting to powdery mildew program"}
            {suggest.defaults.length > 0
              ? <> · Pre-selected: <b>{suggest.defaults.map((d) => `${d.product}${d.activity ? ` (${d.activity})` : ""}`).join(" + ")}</b> — set rates below</>
              : suggest.farming_type === "Organic"
                ? " · No organic-listed materials match — mark OMRI materials on the Approved Materials page"
                : " · No approved materials match these targets"}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Target"><input data-testid="ob-target-input" value={f.target} onChange={(e) => set("target", e.target.value)} className={inp} /></Field>
          <Field label="Reason / Why">
            <select data-testid="ob-reason-select" value={f.reason} onChange={(e) => set("reason", e.target.value)} className={inp}>
              <option value="">— Select Reason —</option>
              {["Preventative", "Pest Present", "Disease Present", "Nutrient", "Weed Control", "Plant Growth Regulator", "Deficiency", "Weather / Disease Model"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Scheduled Date"><input type="date" data-testid="ob-date-input" value={f.date} onChange={(e) => set("date", e.target.value)} className={inp} /></Field>
          <Field label="Planned Start"><input type="time" value={f.planned_start} onChange={(e) => set("planned_start", e.target.value)} className={inp} /></Field>
          <Field label="County"><input value={f.county} onChange={(e) => set("county", e.target.value)} className={inp} /></Field>
          <Field label="Site / Permit ID"><input value={f.site_permit_id} onChange={(e) => set("site_permit_id", e.target.value)} className={inp} /></Field>
        </div>
      </Card>

      <Card title="People">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Manager"><input value={f.manager} onChange={(e) => set("manager", e.target.value)} className={inp} /></Field>
          <Field label="Supervisor"><input value={f.supervisor} onChange={(e) => set("supervisor", e.target.value)} className={inp} /></Field>
          <Field label="Applicator / Driver"><input data-testid="ob-applicator-input" value={f.applicator} onChange={(e) => set("applicator", e.target.value)} className={inp} /></Field>
          <Field label="PCA"><input value={f.pca} onChange={(e) => set("pca", e.target.value)} className={inp} /></Field>
        </div>
      </Card>

      <Card title="Row Pattern">
        <div className="flex flex-wrap gap-2" data-testid="ob-pattern-chips">
          {[["every_row", "Every Row"], ["every_other_row", "Every Other Row"], ["custom", "Custom Rows"]].map(([v, l]) => (
            <button key={v} type="button" data-testid={`ob-pattern-${v}`} onClick={() => set("pattern", v)}
              className={`h-11 px-4 text-sm font-semibold border ${f.pattern === v ? "bg-[#1F3D34] text-white border-[#1F3D34]" : "bg-white border-stone-300"}`}>{l}</button>
          ))}
        </div>
        {f.pattern === "every_other_row" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start Row (required)"><input data-testid="ob-start-row-input" value={f.start_row} onChange={(e) => set("start_row", e.target.value)} className={inp} placeholder="e.g. Row 1" /></Field>
            <Field label="Direction"><input value={f.direction} onChange={(e) => set("direction", e.target.value)} className={inp} placeholder="e.g. odd rows, N→S" /></Field>
          </div>
        )}
        {f.pattern === "custom" && (
          <Field label="Row Numbers"><input value={f.row_numbers} onChange={(e) => set("row_numbers", e.target.value)} className={inp} placeholder="e.g. Rows 1–40, skip 12" /></Field>
        )}
        <Field label={`Treated Acres (auto: ${autoTreated || "—"})`}>
          <input data-testid="ob-treated-acres-input" value={f.treated_acres} onChange={(e) => set("treated_acres", e.target.value)} className={inp} placeholder={String(autoTreated || "")} />
        </Field>
      </Card>

      <Card title="Equipment & Calibration">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tractor"><input value={f.tractor} onChange={(e) => set("tractor", e.target.value)} className={inp} /></Field>
          <Field label="Sprayer"><input value={f.sprayer} onChange={(e) => set("sprayer", e.target.value)} className={inp} /></Field>
          <Field label="Method"><input value={f.method} onChange={(e) => set("method", e.target.value)} className={inp} placeholder="Airblast" /></Field>
          <Field label="Nozzle Setup"><input value={f.nozzle_setup} onChange={(e) => set("nozzle_setup", e.target.value)} className={inp} placeholder="e.g. D5/DC45 ceramic" /></Field>
          <Field label="Nozzle Count"><input value={f.nozzle_count} onChange={(e) => set("nozzle_count", e.target.value)} className={inp} /></Field>
          <Field label="Pressure (PSI)"><input value={f.pressure_psi} onChange={(e) => set("pressure_psi", e.target.value)} className={inp} /></Field>
          <Field label="Speed (MPH)"><input data-testid="ob-speed-input" value={f.speed_mph} onChange={(e) => set("speed_mph", e.target.value)} className={inp} /></Field>
          <Field label="Sprayer Output (GPM)"><input data-testid="ob-gpm-input" value={f.sprayer_gpm} onChange={(e) => set("sprayer_gpm", e.target.value)} className={inp} /></Field>
          <Field label="Row Spacing (ft)"><input data-testid="ob-spacing-input" value={f.row_spacing_ft} onChange={(e) => set("row_spacing_ft", e.target.value)} className={inp} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Target GPA">
            <div className="flex gap-2">
              <select data-testid="ob-gpa-select" value={gpaMode}
                onChange={(e) => { const v = e.target.value; setGpaMode(v); if (v !== "other") set("gpa", v); else set("gpa", ""); }}
                className={`${inp} ${gpaMode === "other" ? "w-28" : ""}`}>
                <option value="50">50 GPA</option>
                <option value="100">100 GPA</option>
                <option value="other">Other…</option>
              </select>
              {gpaMode === "other" && (
                <input data-testid="ob-gpa-input" value={f.gpa} onChange={(e) => set("gpa", e.target.value)} className={inp} placeholder="Enter GPA" />
              )}
            </div>
          </Field>
          <Field label="Tank Size (gal)"><input data-testid="ob-tank-input" value={f.tank_gal} onChange={(e) => set("tank_gal", e.target.value)} className={inp} /></Field>
        </div>
        {calGpa != null && (
          <div className={`text-xs p-3 border font-semibold ${mismatch ? "bg-red-50 border-red-400 text-red-800" : "bg-[#7C9673]/10 border-[#7C9673] text-[#1F3D34]"}`} data-testid="ob-calibration-check">
            Calibration check: 495 × {f.sprayer_gpm} GPM ÷ ({f.speed_mph} MPH × {effSpacing} ft) = <b>{calGpa} GPA</b>
            {mismatch ? ` — differs from target ${nGpa} GPA by >5%. Re-check speed, output or nozzles.` : " — matches target."}
          </div>
        )}
      </Card>

      <Card title="Products">
        {prodRows.map((p, i) => p.kind === "adjuvant" ? null : (
          <div key={i} className="border border-stone-200 p-3 space-y-2" data-testid={`ob-product-row-${i}`}>
            <div className="flex gap-2 items-start">
              <select data-testid={`ob-material-select-${i}`} value={p.material_id}
                onChange={(e) => { const m = materials.find((x) => x.id === e.target.value); setProd(i, { material_id: e.target.value, rate_unit: m?.rate_unit || "" }); }}
                className="flex-1 h-12 px-3 border border-stone-300">
                <option value="">— Approved Material —</option>
                {pesticideMats.map((m) => <option key={m.id} value={m.id}>{recIds.has(m.id) ? "★ " : ""}{m.product}{m.rate_min != null && m.rate_max != null ? ` · Label: ${m.rate_min}–${m.rate_max} ${m.rate_unit || ""}` : " · Label range unverified"}{m.organic ? " · Organic" : ""}{m.frac_group ? ` · FRAC ${m.frac_group}` : ""}</option>)}
              </select>
              {products.length > 1 && (
                <button type="button" onClick={() => setProducts((ps) => ps.filter((_, j) => j !== i))} className="h-12 w-12 border border-stone-300 flex items-center justify-center"><Trash2 size={15} /></button>
              )}
            </div>
            {p.m && (
              <div className="text-sm font-bold text-[#1F3D34] flex flex-wrap items-center gap-2" data-testid={`ob-label-headline-${i}`}>
                {p.m.product}
                {p.m.rate_min != null && p.m.rate_max != null
                  ? <span className="text-xs font-bold bg-[#7C9673]/15 border border-[#7C9673] px-2 py-0.5">Labeled rate: {p.m.rate_min}–{p.m.rate_max} {p.m.rate_unit || ""}</span>
                  : <span className="text-xs font-bold bg-amber-100 border border-amber-400 text-amber-800 px-2 py-0.5">Label range not verified</span>}
                <a href={labelSearchUrl(p.m.product)} target="_blank" rel="noreferrer" className="text-xs underline font-bold">Search &amp; verify label (CDMS)</a>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Field label={`Rate${p.m?.rate_min != null ? ` (${p.m.rate_min}–${p.m.rate_max})` : ""}`}>
                <input data-testid={`ob-rate-input-${i}`} value={p.rate === 0 ? "" : products[i].rate} onChange={(e) => setProd(i, { rate: e.target.value })} className={inp} />
              </Field>
              <Field label="Unit"><input value={p.rate_unit} onChange={(e) => setProd(i, { rate_unit: e.target.value })} className={inp} placeholder={p.m?.rate_unit || "lb/ac"} /></Field>
            </div>
            {p.m && (
              <div className="text-xs text-stone-600">
                {p.m.active_ingredient || "AI not entered"} · REI {p.m.rei_hours ?? "—"}h · PHI {p.m.phi_days ?? "—"}d · Signal: {p.m.signal_word || "—"}
                {p.m.rate_min != null && p.m.rate_max != null ? (
                  <div className="font-bold text-[#1F3D34] mt-0.5" data-testid={`ob-label-range-${i}`}>Label rate (wine grapes): {p.m.rate_min}–{p.m.rate_max} {p.m.rate_unit || ""} · <a href={labelSearchUrl(p.m.product)} target="_blank" rel="noreferrer" className="underline">verify on CDMS</a></div>
                ) : (
                  <div className="font-bold text-amber-700 mt-0.5">No verified label range — confirm on <a href={labelSearchUrl(p.m.product)} target="_blank" rel="noreferrer" className="underline">CDMS label database</a></div>
                )}
                {p.outOfRange && <div className="text-red-700 font-bold mt-1" data-testid={`ob-range-error-${i}`}>Outside the label range {p.m.rate_min}–{p.m.rate_max} {p.m.rate_unit} for wine grapes — the order cannot be created until the rate is in range</div>}
                {p.perTank != null && <div className="mt-1">Per full tank: <b>{p.perTank} {p.rate_unit || p.m.rate_unit || ""}</b> · Total: <b>{p.total} {p.rate_unit || p.m.rate_unit || ""}</b></div>}
              </div>
            )}
          </div>
        ))}
        <button type="button" data-testid="ob-add-product-btn" onClick={() => setProducts((ps) => [...ps, { kind: "pesticide", material_id: "", rate: "", rate_unit: "" }])}
          className="h-11 px-4 border border-stone-300 bg-white font-bold uppercase text-xs flex items-center gap-2"><Plus size={14} /> Add Tank-Mix Product</button>
      </Card>

      <Card title="Adjuvants — Stickers / Spreaders & Drift Control">
        {needDrift && (
          <div className="bg-amber-50 border border-amber-400 p-3 text-xs font-semibold text-amber-900 flex flex-wrap items-center justify-between gap-2" data-testid="ob-drift-prompt">
            <span>{windy ? "Windy forecast" : "Sensitive areas noted"} — consider a drift reduction agent.</span>
            <button type="button" data-testid="ob-add-drift-btn"
              onClick={() => setProducts((ps) => [...ps, { kind: "adjuvant", material_id: driftAgents[0].id, rate: "", rate_unit: driftAgents[0].rate_unit || "" }])}
              className="h-9 px-3 bg-[#1F3D34] text-white font-bold uppercase text-[10px]">Add {driftAgents[0].product}</button>
          </div>
        )}
        {prodRows.map((p, i) => p.kind !== "adjuvant" ? null : (
          <div key={i} className="border border-stone-200 p-3 space-y-2" data-testid={`ob-adjuvant-row-${i}`}>
            <div className="flex gap-2 items-start">
              <select data-testid={`ob-adjuvant-select-${i}`} value={p.material_id}
                onChange={(e) => { const m = materials.find((x) => x.id === e.target.value); setProd(i, { material_id: e.target.value, rate_unit: m?.rate_unit || "" }); }}
                className="flex-1 h-12 px-3 border border-stone-300">
                <option value="">— Adjuvant —</option>
                {adjuvantMats.map((m) => <option key={m.id} value={m.id}>{m.product} · {m.material_type}{m.supplier ? ` · ${m.supplier}` : ""}</option>)}
              </select>
              <button type="button" onClick={() => setProducts((ps) => ps.filter((_, j) => j !== i))} className="h-12 w-12 border border-stone-300 flex items-center justify-center"><Trash2 size={15} /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Rate"><input data-testid={`ob-adjuvant-rate-${i}`} value={products[i].rate} onChange={(e) => setProd(i, { rate: e.target.value })} className={inp} /></Field>
              <Field label="Unit"><input value={p.rate_unit} onChange={(e) => setProd(i, { rate_unit: e.target.value })} className={inp} placeholder="oz/100 gal" /></Field>
            </div>
            {p.m && p.perTank != null && <div className="text-xs text-stone-600">Per full tank: <b>{p.perTank} {(p.rate_unit || "").replace(/\s*\/\s*100\s*gal\s*$/i, "")}</b></div>}
          </div>
        ))}
        <button type="button" data-testid="ob-add-adjuvant-btn" onClick={() => setProducts((ps) => [...ps, { kind: "adjuvant", material_id: "", rate: "", rate_unit: "" }])}
          className="h-11 px-4 border border-stone-300 bg-white font-bold uppercase text-xs flex items-center gap-2"><Plus size={14} /> Add Adjuvant / Sticker-Spreader</button>
        <div className="text-[10px] text-stone-500">Rates in "/100 gal" units are mixed per 100 gallons of spray volume — tank amounts calculate automatically.</div>
      </Card>

      {compat.length > 0 && (
        <div className="bg-red-50 border-2 border-red-500 text-red-800 p-3 text-xs space-y-1" data-testid="ob-compat-warning">
          <div className="font-bold uppercase">Tank-Mix Compatibility</div>
          {compat.map((w, i) => <div key={i} className="font-semibold">• {w}</div>)}
          <div className="text-[10px]">Cross-check in the <a href={LABEL_DB_URL} target="_blank" rel="noreferrer" className="underline font-bold">CDMS label database</a> and each product label. Jar test any new combination.</div>
        </div>
      )}

      {nGpa > 0 && acres > 0 && (
        <div className="bg-[#1F3D34] text-white p-4 text-sm space-y-1" data-testid="ob-calc-preview">
          <div className="font-bold uppercase text-[10px] text-white/60">Tank Plan Preview</div>
          <div>{acres} ac × {nGpa} GPA = <b>{totVol.toLocaleString()} gal</b> total volume</div>
          {nTank > 0 && <div><b>{fullTanks}</b> full {nTank}-gal tanks{finalTank > 0 ? <> + <b>{finalTank} gal</b> final tank</> : ""} · {(nTank / nGpa).toFixed(1)} ac per tank</div>}
        </div>
      )}

      {windows?.days?.length > 0 && (
        <div className="bg-white border border-stone-200 p-3 space-y-1" data-testid="ob-spray-windows">
          <div className="font-bold uppercase text-[10px] text-stone-500">Spray Windows (forecast)</div>
          {windows.days.slice(0, 3).map((d) => (
            <div key={d.date} className="flex flex-wrap items-center gap-1 text-[11px]">
              <span className="font-mono font-bold w-20">{new Date(d.date + "T12:00").toLocaleDateString(undefined, { weekday: "short" })} {d.date.slice(5)}</span>
              {d.windows.map((w, i) => (
                <span key={i} title={w.reasons.join(", ")} className={`px-1.5 py-0.5 font-semibold ${w.rating === "EXCELLENT" ? "bg-[#7C9673] text-white" : w.rating === "ACCEPTABLE" ? "bg-amber-200 text-amber-900" : "bg-red-100 text-red-700"}`}>
                  {w.start}–{w.end + 1}h {w.rating}
                </span>
              ))}
            </div>
          ))}
        </div>
      )}

      <Card title="Instructions & Precautions">
        <Field label="Field Instructions"><textarea value={f.field_instructions} onChange={(e) => set("field_instructions", e.target.value)} rows={2} className="w-full border border-stone-300 p-3 text-sm" placeholder="Shut off at ends, double-cover block edges…" /></Field>
        <Field label="Sensitive Areas"><textarea data-testid="ob-sensitive-input" value={f.sensitive_areas} onChange={(e) => set("sensitive_areas", e.target.value)} rows={2} className="w-full border border-stone-300 p-3 text-sm" placeholder="Houses on west edge, creek along row 40, beehives…" /></Field>
        <Field label="Notes"><input value={f.notes} onChange={(e) => set("notes", e.target.value)} className={inp} /></Field>
      </Card>

      {err && <div className="bg-red-50 border border-red-400 text-red-800 p-3 text-sm font-semibold" data-testid="ob-error">{err}</div>}
      {!valid && missing.length > 0 && (
        <div className="bg-amber-50 border border-amber-400 text-amber-900 p-3 text-xs space-y-1" data-testid="ob-missing-checklist">
          <div className="font-bold uppercase">To create this order:</div>
          {missing.map((m, i) => <div key={i} className="font-semibold">• {m}</div>)}
        </div>
      )}
      <button data-testid="ob-submit-btn" onClick={submit} disabled={busy || !valid}
        className="w-full h-14 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40 flex items-center justify-center gap-2">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <SprayCan size={16} />} Create Work Order (Draft)
      </button>
      <div className="text-[10px] text-stone-500 pb-4">Creates a draft. A manager must review and approve before the field sheet is released for application.</div>
    </div>
  );
}
